import { getFileFromUrl } from "common/utils/file";
import { validateDesignLabArtworkFile } from "common/artwork-file-types";

/**
 * Helper function to download and validate a file from URL
 */
export async function downloadAndValidateFile(
  url: string,
  side: "front" | "back",
  fileContentType: string
): Promise<{ file?: File; success: boolean; error?: string }> {
  try {
    const file = await getFileFromUrl(url, `artwork_image_${side}-${Date.now()}`, fileContentType);
    const { valid, message } = await validateDesignLabArtworkFile(file);

    if (!valid) {
      return { success: false, error: message || "Invalid file" };
    }

    return { file, success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Process multiple file uploads from upload_urls parameter
 */
export async function processMultiFileUpload(
  uploadUrls: string,
  fileContentType: string
): Promise<{
  files: Array<{ sideKey: string; file: File }>;
  errors: Array<{ sideKey: string; error: string }>;
}> {
  const urlsObj = JSON.parse(uploadUrls) as Record<string, string>;
  const downloads: Promise<{
    sideKey: string;
    download: Awaited<ReturnType<typeof downloadAndValidateFile>>;
  }>[] = [];

  // Start all downloads in parallel
  for (const [sideKey, url] of Object.entries(urlsObj)) {
    if (sideKey === "front" || sideKey === "back") {
      downloads.push(
        downloadAndValidateFile(url, sideKey as "front" | "back", fileContentType).then(
          download => ({ sideKey, download })
        )
      );
    }
  }

  // Wait for all downloads to complete
  const downloadResults = await Promise.all(downloads);

  const files: Array<{ sideKey: string; file: File }> = [];
  const errors: Array<{ sideKey: string; error: string }> = [];

  for (const { sideKey, download } of downloadResults) {
    if (download.success && download.file) {
      files.push({ sideKey, file: download.file });
    } else if (download.error) {
      errors.push({ sideKey, error: download.error });
    }
  }

  return { files, errors };
}

/**
 * Process legacy single file upload from artwork_urls parameter
 */
export async function processSingleFileUpload(
  artworkUrls: string,
  fileContentType: string
): Promise<{
  file?: File;
  artworkId?: string;
  error?: string;
}> {
  const urlsObj = JSON.parse(artworkUrls);
  const imageUrl = Object.values(urlsObj).pop() as string;
  const artworkId = Object.keys(urlsObj).pop() as string;

  if (!imageUrl) {
    return { error: "No image URL found" };
  }

  try {
    const file = await getFileFromUrl(imageUrl, `artwork_image-${Date.now()}`, fileContentType);

    const { valid, message } = await validateDesignLabArtworkFile(file);
    if (!valid) {
      return { error: message || "Invalid file" };
    }

    return { file, artworkId };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}
